/**
 * HASHCURVE_WORK_V1 reference implementation (see docs/PROOF_SPEC.md).
 *
 * This module is the *slow, obviously-correct* reference built on viem. Every fast kernel
 * (keccak64.ts, WGSL, native Rust) is checked against it. It is safe to import from Node,
 * workers and browsers (no DOM access at module scope).
 */
import { encodeAbiParameters, encodePacked, keccak256, toBytes } from 'viem';
import type { Address, Hex } from 'viem';

export type { Address, Hex };

/** keccak256("HASHCURVE_WORK_V1") */
export const WORK_DOMAIN: Hex = keccak256(toBytes('HASHCURVE_WORK_V1'));

export const TWO_256 = 1n << 256n;
export const MAX_UINT256 = TWO_256 - 1n;
export const MAX_UINT64 = (1n << 64n) - 1n;

/** Kernels keep the high 192 bits (24 bytes, nonce bytes 0..23) fixed and iterate the low 64 bits. */
export const NONCE_HIGH_BYTES = 24;

export interface SeedParams {
  chainId: bigint | number;
  /** HashcurveLaunch contract address */
  launch: Address;
  challengeId: bigint | number;
  /** current challenge value (bytes32) */
  challenge: Hex;
  /** wallet that will call mineAndBuy (msg.sender) */
  miner: Address;
}

const SEED_ABI = [
  { type: 'bytes32' },
  { type: 'uint256' },
  { type: 'address' },
  { type: 'uint256' },
  { type: 'bytes32' },
  { type: 'address' },
] as const;

/** seed = keccak256(abi.encode(DOMAIN, chainId, launch, challengeId, challenge, miner)) */
export function computeSeed(p: SeedParams): Hex {
  return keccak256(
    encodeAbiParameters(SEED_ABI, [
      WORK_DOMAIN,
      BigInt(p.chainId),
      p.launch,
      BigInt(p.challengeId),
      p.challenge,
      p.miner,
    ]),
  );
}

/** work = keccak256(abi.encodePacked(bytes32 seed, uint256 nonce)) */
export function computeWork(seed: Hex, nonce: bigint | Hex): Hex {
  const n = typeof nonce === 'bigint' ? nonce : BigInt(nonce);
  if (n < 0n || n > MAX_UINT256) throw new RangeError('nonce out of uint256 range');
  return keccak256(encodePacked(['bytes32', 'uint256'], [seed, n]));
}

function asUint256(x: bigint | Hex, what: string): bigint {
  const v = typeof x === 'bigint' ? x : BigInt(x);
  if (v < 0n || v > MAX_UINT256) throw new RangeError(`${what} out of uint256 range`);
  return v;
}

/** A proof is valid iff uint256(work) <= target (work read big-endian). */
export function isValid(work: Hex | bigint, target: bigint | Hex): boolean {
  return asUint256(work, 'work') <= asUint256(target, 'target');
}

/** difficulty := floor(2^256 / (target + 1)) = expected attempts per valid proof. */
export function difficultyFromTarget(target: bigint | Hex): bigint {
  return TWO_256 / (asUint256(target, 'target') + 1n);
}

/**
 * Inverse of difficultyFromTarget: the largest target whose difficulty is >= `difficulty`
 * (i.e. floor(2^256 / d) - 1, clamped to [0, 2^256-1]). difficulty <= 1 => MAX_UINT256.
 */
export function targetFromDifficulty(difficulty: bigint | number): bigint {
  let d: bigint;
  if (typeof difficulty === 'number') {
    if (!Number.isFinite(difficulty) || Number.isNaN(difficulty)) throw new RangeError('difficulty must be finite');
    if (difficulty <= 1) return MAX_UINT256;
    d = BigInt(Math.ceil(difficulty));
  } else {
    d = difficulty;
  }
  if (d <= 1n) return MAX_UINT256;
  const t = TWO_256 / d - 1n;
  return t < 0n ? 0n : t;
}

// ---------------------------------------------------------------------------------------------
// Nonce helpers. nonce = (nonceHigh << 64) | nonceLo, nonceHigh = nonce bytes 0..23 (big-endian).
// ---------------------------------------------------------------------------------------------

export function toBytes32Hex(x: bigint): Hex {
  if (x < 0n || x > MAX_UINT256) throw new RangeError('value out of uint256 range');
  return `0x${x.toString(16).padStart(64, '0')}`;
}

/** Validates & normalises a 24-byte nonce-high hex string. */
export function normalizeNonceHigh(nonceHigh: Hex | bigint): Hex {
  const v = typeof nonceHigh === 'bigint' ? nonceHigh : BigInt(nonceHigh);
  if (v < 0n || v >= 1n << 192n) throw new RangeError('nonceHigh must fit in 24 bytes');
  return `0x${v.toString(16).padStart(48, '0')}`;
}

export function composeNonce(nonceHigh: Hex | bigint, nonceLo: bigint): bigint {
  if (nonceLo < 0n || nonceLo > MAX_UINT64) throw new RangeError('nonceLo must fit in 64 bits');
  return (BigInt(normalizeNonceHigh(nonceHigh)) << 64n) | nonceLo;
}

export function splitNonce(nonce: bigint | Hex): { nonceHigh: Hex; nonceLo: bigint } {
  const n = asUint256(nonce, 'nonce');
  return { nonceHigh: normalizeNonceHigh(n >> 64n), nonceLo: n & MAX_UINT64 };
}

/** Random 24-byte nonce high (per session / worker, so parallel searchers never overlap). */
export function randomNonceHigh(): Hex {
  const b = new Uint8Array(NONCE_HIGH_BYTES);
  globalThis.crypto.getRandomValues(b);
  let s = '0x';
  for (let i = 0; i < b.length; i++) s += b[i]!.toString(16).padStart(2, '0');
  return s as Hex;
}

// ---------------------------------------------------------------------------------------------
// Formatting. Hashrate uses SI prefixes on H/s; difficulty is "expected hashes" (K/M/G/T...).
// ---------------------------------------------------------------------------------------------

const HASHRATE_UNITS = ['H/s', 'kH/s', 'MH/s', 'GH/s', 'TH/s', 'PH/s'] as const;
const COUNT_SUFFIXES = ['', 'K', 'M', 'G', 'T', 'P', 'E'] as const;

function trimFixed(x: number, digits: number): string {
  // 3 significant-ish digits: 1.23, 12.3, 123
  const d = x >= 100 ? Math.max(0, digits - 2) : x >= 10 ? Math.max(0, digits - 1) : digits;
  return x.toFixed(d);
}

/** e.g. 1_234_567 -> "1.23 MH/s". Non-finite or negative -> "0 H/s". */
export function formatHashrate(hashesPerSecond: number, digits = 2): string {
  if (!Number.isFinite(hashesPerSecond) || hashesPerSecond <= 0) return '0 H/s';
  let v = hashesPerSecond;
  let i = 0;
  while (v >= 1000 && i < HASHRATE_UNITS.length - 1) {
    v /= 1000;
    i++;
  }
  if (i === 0) return `${Math.round(v)} H/s`;
  // avoid "1000.00 kH/s" after rounding
  if (Number(trimFixed(v, digits)) >= 1000 && i < HASHRATE_UNITS.length - 1) {
    v /= 1000;
    i++;
  }
  return `${trimFixed(v, digits)} ${HASHRATE_UNITS[i]}`;
}

function bigToNumber(x: bigint | number): number {
  return typeof x === 'number' ? x : Number(x);
}

/** Expected hashes with K/M/G/T/P/E suffix, e.g. 65536n -> "65.5K". Beyond E: "1.23e+21". */
export function formatHashCount(count: bigint | number, digits = 2): string {
  let v = bigToNumber(count);
  if (!Number.isFinite(v) || v < 0) return String(count);
  if (v < 1000) return String(Math.round(v));
  let i = 0;
  while (v >= 1000 && i < COUNT_SUFFIXES.length - 1) {
    v /= 1000;
    i++;
  }
  if (v >= 1000) return bigToNumber(count).toExponential(digits);
  if (Number(trimFixed(v, digits)) >= 1000) {
    if (i === COUNT_SUFFIXES.length - 1) return bigToNumber(count).toExponential(digits);
    v /= 1000;
    i++;
  }
  return `${trimFixed(v, digits)}${COUNT_SUFFIXES[i]}`;
}

/** Difficulty (= expected hashes per valid proof), e.g. "4.19M". */
export function formatDifficulty(difficulty: bigint | number, digits = 2): string {
  return formatHashCount(difficulty, digits);
}

/** Expected seconds to find a proof at `hashesPerSecond` (Infinity if rate is 0). */
export function expectedSeconds(difficulty: bigint | number, hashesPerSecond: number): number {
  if (!(hashesPerSecond > 0)) return Infinity;
  return bigToNumber(difficulty) / hashesPerSecond;
}

/** Human duration for expected-time displays: "850 ms", "12.3 s", "4.2 min", "3.1 h", "2.0 d". */
export function formatDuration(seconds: number): string {
  if (!Number.isFinite(seconds)) return '∞';
  if (seconds < 1) return `${Math.round(seconds * 1000)} ms`;
  if (seconds < 60) return `${seconds.toFixed(1)} s`;
  if (seconds < 3600) return `${(seconds / 60).toFixed(1)} min`;
  if (seconds < 86400) return `${(seconds / 3600).toFixed(1)} h`;
  return `${(seconds / 86400).toFixed(1)} d`;
}
